/**
 * Rate Change Preview Modal
 * Displays a unified comparison table showing current vs proposed schedule
 * with impacted rows highlighted
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Card,
  CardContent,
  Grid,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { apiPost } from '../../utils/apiClient';
import { formatCurrency as formatCurrencyBase, formatNumber } from '../../utils/formatters';
import { useTenant } from '../../contexts/TenantContext';

function RateChangePreviewModal({
  open,
  onClose,
  rateChangeId,
  onApprove,
  currencyCode = 'ZAR',
  // Pre-fetched preview payload. When supplied, the modal uses it directly and
  // skips the internal fetch — used by the adhoc submit-time preview where no
  // contract_rate_changes row exists yet to load by id.
  previewData: previewDataProp,
  // Hide the "Approve Rate Change" CTA. Used in the same adhoc submit-time
  // context — the row hasn't been created yet, so there's nothing to approve.
  showApprove = true,
  // When true, render a slim LinearProgress strip at the top of the dialog
  // while a background refresh (e.g. adhoc form blur auto-refresh) is in
  // flight. The existing previewData stays rendered underneath so the user
  // doesn't lose context.
  refreshing = false,
}) {
  const { tenant } = useTenant();
  const dp = tenant?.roundingPrecision ?? 3;
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [ackReversal, setAckReversal] = useState(false);

  useEffect(() => {
    if (!open) {
      setAckReversal(false);
      return;
    }
    // Pre-fetched mode: caller passed previewData directly. Adopt it and skip
    // the network fetch.
    if (previewDataProp) {
      setPreviewData(previewDataProp);
      setLoading(false);
      setError(null);
      setAckReversal(false);
      return;
    }
    if (rateChangeId) {
      fetchPreview();
      setAckReversal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchPreview is defined below and runs on open/rateChangeId/previewDataProp change
  }, [open, rateChangeId, previewDataProp]);

  const fetchPreview = async () => {
    setLoading(true);
    setError(null);
    setPreviewData(null);
    try {
      const response = await apiPost(`/rate-changes/${rateChangeId}/preview`);

      if (!response?.success || !response?.data) {
        throw new Error(response?.message || 'Invalid response from server');
      }

      const data = response.data;
      if (!data.rate_change || !data.preview_schedule) {
        throw new Error('Preview data is incomplete');
      }

      setPreviewData(data);
    } catch (err) {
      console.error('Preview fetch error:', err);
      setError(err.message || 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await onApprove();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to approve rate change');
    } finally {
      setApproving(false);
    }
  };

  const formatCurrency = (value) => formatCurrencyBase(value, currencyCode, dp);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const str = String(dateStr).substring(0, 10);
      return format(new Date(str + 'T12:00:00'), 'dd MMM yyyy');
    } catch {
      return '-';
    }
  };

  const formatDifference = (diff, isPercentage = false) => {
    if (!diff && diff !== 0) return '-';
    const num = parseFloat(diff);
    const sign = num >= 0 ? '+' : '';
    if (isPercentage) {
      return `${sign}${num.toFixed(2)}%`;
    }
    return `${sign}${currencyCode} ${formatNumber(Math.abs(num), 2)}`;
  };

  const getFlowType = (narration) => {
    if (!narration) return 'neutral';
    if (narration.includes('Drawdown') || narration.includes('Opening Balance')) return 'inflow';
    if (narration.includes('Payment') || narration.includes('Repayment') || narration.includes('Withdrawal')) return 'outflow';
    return 'neutral';
  };

  const formatAmount = (value) => {
    if (value == null) return '-';
    const num = parseFloat(value);
    if (num === 0) return '-';
    return formatNumber(num, 2);
  };

  // Format amount with flow direction sign (negative for outflows, positive for inflows)
  const formatFlowAmount = (value, flowType) => {
    if (value == null) return '-';
    const num = parseFloat(value);
    if (num === 0) return '-';
    const formatted = formatNumber(num, 2);
    if (flowType === 'outflow') {
      return <span style={{ color: '#d32f2f' }}>({formatted})</span>;
    }
    if (flowType === 'inflow') {
      return <span style={{ color: '#1976d2' }}>{formatted}</span>;
    }
    return formatted;
  };

  /**
   * Match any kind of interest payment narration, regular or adhoc. The schedule
   * uses both 'Interest Payment' and 'Adhoc Interest Payment' — both behave the
   * same in the comparison view: they cap an accrual period and should display
   * a proposed value derived from accumulated proposed gross.
   */
  const isInterestPaymentNarration = (narration) => {
    if (!narration) return false;
    return narration === 'Interest Payment' || narration === 'Adhoc Interest Payment';
  };

  /**
   * Check if a narration is an interest-related type (accrual or payment, regular or adhoc).
   */
  const isInterestNarration = (narration) => {
    if (!narration) return false;
    return narration.startsWith('Interest Accrual') || isInterestPaymentNarration(narration);
  };

  /**
   * Pre-process schedule to inject synthetic monthly Interest Accrual entries
   * for any Interest Payment that has no preceding accruals in its period.
   * This handles older contracts where only payment rows exist in the DB.
   */
  const injectSyntheticAccruals = (schedule, dayCountBasis = 365) => {
    const msPerDay = 86400000;

    /**
     * Helper: split a multi-month period into monthly sub-entries.
     * Steps from periodStart to periodEnd using same-day-of-month boundaries
     * (matching the canonical addMonths pattern in generateAmortizationSchedule).
     */
    const splitIntoMonthlyEntries = (periodStartObj, periodEndObj, entry, preserveNarration) => {
      const principal = parseFloat(entry.outstanding_balance || 0);
      const rate = parseFloat(entry.interest_rate || 0);
      const grossTotal = parseFloat(entry.gross_interest_amount || entry.interest_amount || 0);
      const subEntries = [];
      let accumulated = 0;
      let accrualStart = new Date(periodStartObj);

      while (accrualStart < periodEndObj) {
        let monthEnd = new Date(Date.UTC(
          accrualStart.getUTCFullYear(),
          accrualStart.getUTCMonth() + 1,
          accrualStart.getUTCDate()
        ));
        if (monthEnd > periodEndObj) {
          monthEnd = new Date(periodEndObj);
        }

        const daysInPeriod = Math.round((monthEnd - accrualStart) / msPerDay);
        if (daysInPeriod > 0) {
          const grossInterest = principal * (rate / 100) * (daysInPeriod / dayCountBasis);
          accumulated += grossInterest;
          const isLast = monthEnd.getTime() === periodEndObj.getTime();

          subEntries.push({
            payment_date: monthEnd.toISOString().substring(0, 10),
            narration: (isLast && preserveNarration) ? (entry.narration || 'Interest Accrual') : 'Interest Accrual',
            days_in_period: daysInPeriod,
            outstanding_balance: principal,
            interest_rate: rate,
            gross_interest_amount: grossInterest,
            interest_amount: grossInterest,
            net_interest_amount: grossInterest,
            capital_amount: 0,
            payment_amount: 0,
            wht_rate_applied: entry.wht_rate_applied,
            wht_gross_up: entry.wht_gross_up,
          });
        }

        accrualStart = new Date(monthEnd);
      }

      // Scale so sub-entries sum to the original total
      if (accumulated > 0 && grossTotal > 0) {
        const scaleFactor = grossTotal / accumulated;
        subEntries.forEach(sa => {
          sa.gross_interest_amount *= scaleFactor;
          sa.interest_amount = sa.gross_interest_amount;
          sa.net_interest_amount = sa.gross_interest_amount;
        });
      }

      return subEntries;
    };

    const enriched = [];
    let lastPaymentDateStr = null;

    for (let i = 0; i < schedule.length; i++) {
      const entry = schedule[i];
      const narration = entry.narration || '';

      // 1) Handle multi-month Interest Accrual entries (e.g., old lump "Pre-Rate Change" entries)
      if (narration.startsWith('Interest Accrual') && parseInt(entry.days_in_period) > 35) {
        const entryDateStr = String(entry.payment_date || '').substring(0, 10);
        const entryDateObj = new Date(entryDateStr + 'T00:00:00Z');
        const days = parseInt(entry.days_in_period);

        // The entry's period start based on its days_in_period
        const rawPeriodStart = new Date(entryDateObj);
        rawPeriodStart.setUTCDate(rawPeriodStart.getUTCDate() - days);

        // Check if earlier entries already cover part of this period.
        // Find the last accrual or payment date already in enriched — that's the true period start.
        let truePeriodStart = rawPeriodStart;
        for (let j = enriched.length - 1; j >= 0; j--) {
          const prevDate = String(enriched[j].payment_date || '').substring(0, 10);
          if (prevDate && prevDate > rawPeriodStart.toISOString().substring(0, 10)) {
            truePeriodStart = new Date(prevDate + 'T00:00:00Z');
            break;
          }
        }

        const correctedDays = Math.round((entryDateObj - truePeriodStart) / msPerDay);

        if (correctedDays <= 35) {
          // Period is now single-month after trimming overlap — just fix days_in_period
          const principal = parseFloat(entry.outstanding_balance || 0);
          const rate = parseFloat(entry.interest_rate || 0);
          const correctedInterest = principal * (rate / 100) * (correctedDays / dayCountBasis);
          enriched.push({
            ...entry,
            days_in_period: correctedDays,
            gross_interest_amount: correctedInterest,
            interest_amount: correctedInterest,
            net_interest_amount: correctedInterest,
          });
        } else {
          // Still multi-month — split into monthly sub-entries from true start
          const subEntries = splitIntoMonthlyEntries(truePeriodStart, entryDateObj, entry, true);
          if (subEntries.length > 0) {
            enriched.push(...subEntries);
          } else {
            enriched.push(entry);
          }
        }
        continue;
      }

      // 2) For non-Interest-Payment entries, pass through as-is
      if (!narration.startsWith('Interest Payment')) {
        enriched.push(entry);
        continue;
      }

      // 3) Interest Payment — inject synthetic accruals if none exist for this period
      const paymentDateStr = String(entry.payment_date || '').substring(0, 10);

      const hasAccruals = enriched.some(e => {
        const n = e.narration || '';
        const d = String(e.payment_date || '').substring(0, 10);
        return n.startsWith('Interest Accrual')
          && (!lastPaymentDateStr || d > lastPaymentDateStr)
          && d <= paymentDateStr;
      });

      if (!hasAccruals) {
        const principal = parseFloat(entry.outstanding_balance || 0);
        const rate = parseFloat(entry.interest_rate || 0);

        let periodStartStr = lastPaymentDateStr;
        if (!periodStartStr && schedule.length > 0) {
          periodStartStr = String(schedule[0].payment_date || '').substring(0, 10);
        }

        if (periodStartStr && principal > 0 && rate > 0) {
          const periodStartObj = new Date(periodStartStr + 'T00:00:00Z');
          const paymentDateObj = new Date(paymentDateStr + 'T00:00:00Z');

          const subEntries = splitIntoMonthlyEntries(periodStartObj, paymentDateObj, entry, false);
          enriched.push(...subEntries);
        }
      }

      enriched.push(entry);
      lastPaymentDateStr = paymentDateStr;
    }

    return enriched;
  };

  /**
   * Build display rows using the current schedule as the base.
   * For interest entries at/after the effective date, calculate the proposed interest
   * directly using the entry's days_in_period and outstanding_balance with the new rate.
   * This avoids any matching issues between current and preview schedules.
   */
  const buildDisplayRows = (currentSchedule, effectiveDate, oldRate, newRate, dayCountBasis = 365) => {
    const current = currentSchedule || [];
    const enrichedSchedule = injectSyntheticAccruals(current, dayCountBasis);
    const rows = [];
    const effectiveDateStr = effectiveDate ? String(effectiveDate).substring(0, 10) : '';
    const effectiveDateObj = effectiveDateStr ? new Date(effectiveDateStr + 'T00:00:00Z') : null;
    const oldRateNum = parseFloat(oldRate || 0);
    const newRateNum = parseFloat(newRate || 0);

    // Get WHT info from the first payment entry that has it
    const paymentEntry = current.find(e => e.narration?.startsWith('Interest Payment') && e.wht_rate_applied != null);
    const whtRateNum = parseFloat(paymentEntry?.wht_rate_applied || 0);
    const whtGrossUp = paymentEntry?.wht_gross_up || false;

    // Track accumulated proposed gross interest for payment calculation.
    let accumulatedProposedGross = 0;
    // Track whether we've already handled the split month
    let splitHandled = false;

    enrichedSchedule.forEach(entry => {
      const entryDate = String(entry.payment_date || '').substring(0, 10);
      const isAfterEffective = effectiveDateStr && entryDate >= effectiveDateStr;
      const narration = entry.narration || '';
      const isInterest = isInterestNarration(narration);

      if (!isInterest) {
        // Non-interest entry: show as-is
        const curInt = parseFloat(entry.interest_amount || entry.net_interest_amount || 0);
        const capAmt = parseFloat(entry.capital_amount || 0);
        rows.push({
          payment_date: entry.payment_date,
          narration: narration,
          current_rate: entry.interest_rate,
          proposed_rate: null,
          days_in_period: null,
          current_interest: entry.interest_amount || entry.net_interest_amount,
          proposed_interest: null,
          capital_amount: entry.capital_amount,
          current_total: (curInt + capAmt) || null,
          proposed_total: null,
          is_impacted: false,
          flow_type: getFlowType(narration),
        });
      } else if (!isAfterEffective && narration.startsWith('Interest Accrual')) {
        // Pre-effective accrual: accumulate for the next payment
        const grossInterest = parseFloat(entry.gross_interest_amount || entry.interest_amount || 0);
        accumulatedProposedGross += grossInterest;

        const curInt = parseFloat(entry.interest_amount || entry.net_interest_amount || 0);
        const capAmt = parseFloat(entry.capital_amount || 0);
        rows.push({
          payment_date: entry.payment_date,
          narration: narration,
          current_rate: entry.interest_rate,
          proposed_rate: null,
          days_in_period: entry.days_in_period,
          current_interest: entry.interest_amount || entry.net_interest_amount,
          proposed_interest: null,
          capital_amount: entry.capital_amount,
          current_total: (curInt + capAmt) || null,
          proposed_total: null,
          is_impacted: false,
          flow_type: getFlowType(narration),
        });
      } else if (!isAfterEffective && isInterestPaymentNarration(narration)) {
        // Pre-effective payment (regular or adhoc): reset accumulator
        accumulatedProposedGross = 0;

        const curInt = parseFloat(entry.interest_amount || entry.net_interest_amount || 0);
        const capAmt = parseFloat(entry.capital_amount || 0);
        rows.push({
          payment_date: entry.payment_date,
          narration: narration,
          current_rate: entry.interest_rate,
          proposed_rate: null,
          days_in_period: null,
          current_interest: entry.interest_amount || entry.net_interest_amount,
          proposed_interest: null,
          capital_amount: entry.capital_amount,
          current_total: (curInt + capAmt) || null,
          proposed_total: null,
          is_impacted: false,
          flow_type: getFlowType(narration),
        });
      } else if (narration.startsWith('Interest Accrual')) {
        // Post-effective accrual: check if this entry spans the effective date (mid-month split)
        const days = parseInt(entry.days_in_period) || 0;
        const principal = parseFloat(entry.outstanding_balance || entry.capital_amount || 0);
        const entryDateObj = new Date(entryDate + 'T00:00:00Z');

        // Calculate period start: payment_date - days_in_period
        const periodStartObj = new Date(entryDateObj);
        periodStartObj.setUTCDate(periodStartObj.getUTCDate() - days);
        const periodStartStr = periodStartObj.toISOString().substring(0, 10);

        // Check if this accrual spans the effective date (period starts before, ends at/after)
        const spansEffective = !splitHandled
          && effectiveDateObj
          && periodStartStr < effectiveDateStr
          && entryDate >= effectiveDateStr;

        if (spansEffective && days > 0) {
          // Split into two rows: pre-change and post-change
          splitHandled = true;

          // Calculate days for each portion
          const msPerDay = 86400000;
          const preChangeDays = Math.round((effectiveDateObj - periodStartObj) / msPerDay);
          const postChangeDays = days - preChangeDays;

          // Use the entry's own rate (not the rate change's old_rate) for historical accuracy
          const entryRateNum = parseFloat(entry.interest_rate || 0);

          if (preChangeDays > 0) {
            // Row 1: Pre-change portion (entry's own rate, unchanged in proposed)
            const preGross = principal * (entryRateNum / 100) * (preChangeDays / dayCountBasis);
            accumulatedProposedGross += preGross; // old rate portion stays the same

            rows.push({
              payment_date: effectiveDateStr,
              narration: 'Interest Accrual (Pre-Rate Change)',
              current_rate: entry.interest_rate,
              proposed_rate: entryRateNum,
              days_in_period: preChangeDays,
              current_interest: preGross,
              proposed_interest: preGross, // same - unchanged
              capital_amount: 0,
              current_total: preGross,
              proposed_total: preGross,
              is_impacted: false,
              flow_type: 'accrual',
              is_split: true,
            });
          }

          if (postChangeDays > 0) {
            // Row 2: Post-change portion (recalculated at new rate)
            const currentPostGross = principal * (entryRateNum / 100) * (postChangeDays / dayCountBasis);
            const proposedPostGross = principal * (newRateNum / 100) * (postChangeDays / dayCountBasis);
            accumulatedProposedGross += proposedPostGross;

            const isImpacted = Math.abs(currentPostGross - proposedPostGross) > 0.01;

            rows.push({
              payment_date: entry.payment_date,
              narration: 'Interest Accrual',
              current_rate: entry.interest_rate,
              proposed_rate: newRateNum,
              days_in_period: postChangeDays,
              current_interest: currentPostGross,
              proposed_interest: proposedPostGross,
              capital_amount: 0,
              current_total: currentPostGross,
              proposed_total: proposedPostGross,
              is_impacted: isImpacted,
              flow_type: 'accrual',
              is_split: true,
            });
          }
        } else {
          // Normal post-effective accrual (no split needed)
          const proposedGross = principal * (newRateNum / 100) * (days / dayCountBasis);
          accumulatedProposedGross += proposedGross;

          const currentInterest = parseFloat(entry.interest_amount || entry.net_interest_amount || 0);
          const isImpacted = Math.abs(currentInterest - proposedGross) > 0.01;

          const capAmt = parseFloat(entry.capital_amount || 0);
          rows.push({
            payment_date: entry.payment_date,
            narration: narration,
            current_rate: entry.interest_rate,
            proposed_rate: newRateNum,
            days_in_period: entry.days_in_period,
            current_interest: entry.interest_amount || entry.net_interest_amount,
            proposed_interest: proposedGross,
            capital_amount: entry.capital_amount,
            current_total: (currentInterest + capAmt) || null,
            proposed_total: (proposedGross + capAmt) || null,
            is_impacted: isImpacted,
            flow_type: getFlowType(narration),
          });
        }
      } else if (isInterestPaymentNarration(narration)) {
        // Post-effective payment (regular or adhoc): proposed = accumulated
        // proposed gross interest, adjusted for WHT.
        let proposedNet = accumulatedProposedGross;
        if (whtRateNum > 0 && !whtGrossUp) {
          proposedNet = accumulatedProposedGross * (1 - whtRateNum / 100);
        }

        const currentInterest = parseFloat(entry.interest_amount || entry.net_interest_amount || 0);
        const isImpacted = Math.abs(currentInterest - proposedNet) > 0.01;

        const capAmt = parseFloat(entry.capital_amount || 0);
        rows.push({
          payment_date: entry.payment_date,
          narration: narration,
          current_rate: entry.interest_rate,
          proposed_rate: newRateNum,
          days_in_period: null,
          current_interest: entry.interest_amount || entry.net_interest_amount,
          proposed_interest: proposedNet,
          capital_amount: entry.capital_amount,
          current_total: (currentInterest + capAmt) || null,
          proposed_total: (proposedNet + capAmt) || null,
          is_impacted: isImpacted,
          flow_type: getFlowType(narration),
        });

        // Reset accumulator after payment
        accumulatedProposedGross = 0;
      }
    });

    return rows;
  };

  const getRowBackground = (row, isIncrease) => {
    if (!row.is_impacted) return {};
    return { backgroundColor: isIncrease ? '#fff8e1' : '#e8f5e9' };
  };

  const rateChange = previewData?.rate_change;
  const comparison = previewData?.comparison;
  const isIncrease = rateChange && rateChange.new_rate > rateChange.old_rate;

  const comparisonRows = previewData
    ? buildDisplayRows(
        previewData.current_schedule,
        rateChange?.effective_date,
        rateChange?.old_rate,
        rateChange?.new_rate,
        rateChange?.day_count_basis || 365
      )
    : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Rate Change Impact Preview
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {refreshing && <LinearProgress />}
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && previewData && (
          <>
            {/* Rate Change Summary */}
            <Alert
              severity="info"
              icon={isIncrease ? <TrendingUpIcon /> : <TrendingDownIcon />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2">
                <strong>Rate Change:</strong> {rateChange.old_rate.toFixed(4)}% → {rateChange.new_rate.toFixed(4)}%
                {' '}(effective {formatDate(rateChange.effective_date)})
                <br />
                <strong>Reason:</strong> {rateChange.reason || 'Not specified'}
              </Typography>
            </Alert>

            {/* Backdated change touches journal-exported periods: warn + require ack */}
            {previewData?.affects_exported_periods && (
              <Alert
                severity="warning"
                icon={<WarningIcon />}
                sx={{ mb: 2 }}
              >
                <AlertTitle>This change affects journal-exported periods</AlertTitle>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {previewData.exported_periods.length} accrual period{previewData.exported_periods.length === 1 ? '' : 's'} {previewData.exported_periods.length === 1 ? 'has' : 'have'} already been journal-exported.
                  The original entries will be preserved. On approval, an <strong>Interest Reversal</strong> + <strong>Interest Re-accrual</strong>
                  {' '}pair will be generated for each so the GL is corrected on the next export.
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Period</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Original</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Corrected</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Net adjustment</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Matched payment</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewData.exported_periods.map((p) => (
                        <TableRow key={p.schedule_id}>
                          <TableCell>{formatDate(p.payment_date)}</TableCell>
                          <TableCell align="right">{formatCurrency(p.original_amount)}</TableCell>
                          <TableCell align="right">
                            {p.new_amount == null
                              ? <em>unmatched in preview</em>
                              : formatCurrency(p.new_amount)}
                          </TableCell>
                          <TableCell align="right">
                            {p.net_adjustment == null
                              ? '-'
                              : formatDifference(p.net_adjustment)}
                          </TableCell>
                          <TableCell>
                            {p.has_matched_payment
                              ? <Chip size="small" color="warning" label="Matched" />
                              : <Chip size="small" label="Unmatched" />}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            {/* Subset that ALSO has a matched payment -- extra callout */}
            {previewData?.exported_periods_matched?.length > 0 && (
              <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                <AlertTitle>
                  {previewData.exported_periods_matched.length} of those period{previewData.exported_periods_matched.length === 1 ? '' : 's'}
                  {' '}also {previewData.exported_periods_matched.length === 1 ? 'has' : 'have'} a matched payment
                </AlertTitle>
                <Typography variant="body2">
                  Generating reversal entries will <strong>not</strong> unmatch the payment(s).
                  You may need to re-evaluate the match against the corrected accrual after the next journal export.
                </Typography>
              </Alert>
            )}

            {/* Acknowledgement gate */}
            {previewData?.affects_exported_periods && showApprove && (
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ackReversal}
                      onChange={(e) => setAckReversal(e.target.checked)}
                    />
                  }
                  label="I understand — generate reversal + re-accrual entries for the affected exported periods."
                />
              </Box>
            )}

            {/* Impact Summary */}
            {comparison && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Impact Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid
                      size={{
                        xs: 12,
                        md: 4
                      }}>
                      <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Current Total Interest
                        </Typography>
                        <Typography variant="h6">
                          {formatCurrency(comparison.current.total_interest)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 4
                      }}>
                      <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Proposed Total Interest
                        </Typography>
                        <Typography variant="h6" color={isIncrease ? 'error.main' : 'success.main'}>
                          {formatCurrency(comparison.preview.total_interest)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 4
                      }}>
                      <Box sx={{
                        textAlign: 'center',
                        p: 1,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: isIncrease ? '#ffebee' : '#e8f5e9',
                      }}>
                        <Typography variant="caption" color="text.secondary">
                          Difference
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Typography variant="h6" color={isIncrease ? 'error.main' : 'success.main'}>
                            {formatDifference(comparison.difference.total_interest)}
                          </Typography>
                          <Chip
                            label={formatDifference(comparison.difference.percentage_change, true)}
                            size="small"
                            color={isIncrease ? 'error' : 'success'}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  {comparison.current.total_wht > 0 || comparison.preview.total_wht > 0 ? (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        WHT Impact: {formatCurrency(comparison.current.total_wht)} → {formatCurrency(comparison.preview.total_wht)}
                        ({formatDifference(comparison.difference.total_wht)})
                      </Typography>
                    </Box>
                  ) : null}
                </CardContent>
              </Card>
            )}

            {/* Unified Comparison Table */}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }} align="right">Current Rate</TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }} align="right">Proposed Rate</TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }} align="right">Days</TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }} align="right">Current Interest</TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }} align="right">Proposed Interest</TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }} align="right">Capital</TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }} align="right">Current Total</TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }} align="right">Proposed Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisonRows.map((row, idx) => (
                    <TableRow key={idx} sx={getRowBackground(row, isIncrease)}>
                      <TableCell>{formatDate(row.payment_date)}</TableCell>
                      <TableCell>{row.narration}</TableCell>
                      <TableCell align="right">
                        {row.current_rate != null ? `${parseFloat(row.current_rate).toFixed(2)}%` : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {row.proposed_rate != null ? `${parseFloat(row.proposed_rate).toFixed(2)}%` : '-'}
                      </TableCell>
                      <TableCell align="right">{row.days_in_period || '-'}</TableCell>
                      <TableCell align="right">{formatFlowAmount(row.current_interest, row.flow_type)}</TableCell>
                      <TableCell align="right">{formatFlowAmount(row.proposed_interest, row.flow_type)}</TableCell>
                      <TableCell align="right">{formatFlowAmount(row.capital_amount, row.flow_type)}</TableCell>
                      <TableCell align="right">{formatFlowAmount(row.current_total, row.flow_type)}</TableCell>
                      <TableCell align="right">{formatFlowAmount(row.proposed_total, row.flow_type)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={approving}>
          Close
        </Button>
        {showApprove && (
          <Button
            variant="contained"
            color="success"
            startIcon={approving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            onClick={handleApprove}
            disabled={
              loading
              || approving
              || !previewData
              || (previewData?.affects_exported_periods && !ackReversal)
            }
          >
            {approving
              ? 'Approving...'
              : previewData?.affects_exported_periods
                ? 'Confirm — generate reversal entries'
                : 'Approve Rate Change'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default RateChangePreviewModal;
